import React from 'react';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { useQuery } from '@apollo/client';
import { firstDayOfPrevMonth, lastDayOfPrevMonth } from '@perch-labs/warbler';
import {
  Grid,
  LeftColumnGridCell,
  RightColumnGridCell,
  MobileGridCell,
} from 'styles';
import { screenIsSmall } from 'utils/screen-is-small';
import { ErrorPage } from 'components/errors';
import { LoadingModule } from 'components/loading';
import { PersonalEnergyProduction } from 'pages/solar/components/personal-energy-production';
import { StateSolarFarms } from 'pages/solar/components/state-solar-farms';
import { EnergyBreakdown } from 'pages/solar/components/energy-breakdown';
import { PageHeading } from 'components/page-heading';
import {
  EnrollmentTimeline,
  EnrollmentStatus,
} from 'components/enrollment-timeline';
import { AdvocacyModule } from 'pages/solar/components/advocacy-module';
import {
  ProjectDetails,
  ProjectStatistics,
} from 'pages/solar/components/project-details';
import { ReconnectUtilityNotification } from 'pages/solar/components/reconnect-utility-notification';
import { UserRewards } from 'pages/solar/components/user-rewards';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSessionContext } from 'contexts/session';
import { GET_USER_REWARD_STATUS } from 'pages/solar/components/user-rewards/user-rewards.api';
import {
  getProjectDetailsProps,
  Project,
} from 'pages/solar/components/project-details/production-graph.utils';
import {
  getPersonalEnergyProductionCopy,
  getSolarCredit,
} from './enrolled.utils';
import { GET_ENROLLED_VNM_DATA, GET_VNM_PLAN } from './enrolled.api';
import { showRewardsModule } from '../active.utils';

const Loading = ({ header }) => (
  <>
    {header}
    <Grid data-testid="loading" width="100%" gap="24px">
      <LeftColumnGridCell>
        <LoadingModule />
        <LoadingModule margin={{ top: '24px' }} />
      </LeftColumnGridCell>
      <RightColumnGridCell>
        <LoadingModule />
        <LoadingModule margin={{ top: '24px' }} />
      </RightColumnGridCell>
    </Grid>
  </>
);

export const Enrolled: React.FC<SolarActiveViewProps> = props => {
  const {
    advocacyCampaign,
    completedAdvocacyCampaign,
    hasAdvocacyCampaignSubmission,
    pageHeading,
    stateAbbreviation,
    stateFullName,
    stateId,
    vnmSolarProjectsStatistics,
    zipCode,
    utilityAccountId,
    originationSourceType,
  } = props;
  const { width } = useWindowSize();
  const flags = useFlags();
  const { planId } = useParams<{ planId?: string }>();
  const plan = useQuery<GetVnmPlanDataQuery, GetVnmPlanDataQueryVariables>(
    GET_VNM_PLAN,
    {
      skip: !planId,
      variables: { planId: Number(planId) || -1 },
    }
  );

  const { userId } = useSessionContext();
  const {
    data: userStatus,
    error: userStatusError,
    loading: userStatusLoading,
  } = useQuery(GET_USER_REWARD_STATUS, {
    variables: { userId },
  });

  const startDate =
    plan.data?.plan.mostRecentStatement?.singleUtilityStatement
      ?.serviceStartDate || firstDayOfPrevMonth();
  const endDate =
    plan.data?.plan.mostRecentStatement?.singleUtilityStatement
      ?.serviceEndDate || lastDayOfPrevMonth();
  const cumulativeVnmSavings = plan.data?.plan.cumulativeVnmSavings;

  const { data, error, loading } = useQuery<
    GetVnmEnrolledSolarProjectDataQuery,
    GetVnmEnrolledSolarProjectDataQueryVariables
  >(GET_ENROLLED_VNM_DATA, {
    skip: !planId,
    variables: { planId: Number(planId) || -1 },
  });

  const project =
    data?.plan?.vnmProductSubscription?.vnmSubscription?.vnmSolarProject;

  const header = <PageHeading page={pageHeading} />;

  if (loading || plan.loading) return <Loading header={header} />;
  if (error || plan.error) return <ErrorPage />;

  // We can assert that data is defined because we have handled the error & loading states above
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { billable, vnmProductSubscription } = data!.plan;
  const vnmProductionKwh =
    plan.data?.plan.mostRecentStatement?.singleUtilityStatement
      ?.vnmProductionKwh;

  const vnmSubscription = vnmProductSubscription?.vnmSubscription;

  // If we can read VNM production kwh from the utility statement, use that
  // 15th October 2025:
  // previously, this was falling back to inverter data from
  // GoldFinch(which got it from Falcon).
  // but that API is obselete and is no longer in use and was not
  // migrated during perch AWS. You might end up seeing 0 if utility statement
  // doesn't have kwh going forward. If that is a problem,
  //  Leaving this comment for future debugging
  const kwh = vnmProductionKwh || 0;

  const credit = getSolarCredit(
    plan.data?.plan.mostRecentStatement?.lineItems || []
  );
  // TODO: type this function to take in the generated type and remove cast
  const projectDetailsProps = getProjectDetailsProps(project! as Project);

  const calculatedAllocationKw =
    vnmSubscription?.mostRecentSubscriptionSubmission?.calculatedAllocationKw;

  const personalEnergyProductionCopy = getPersonalEnergyProductionCopy({
    credit,
    endDate,
    startDate,
    calculatedAllocationKw,
  });

  const personalEnergyProduction = credit ? (
    <PersonalEnergyProduction
      billable={billable}
      copy={personalEnergyProductionCopy}
      cumulativeVnmSavings={cumulativeVnmSavings}
      kwh={kwh}
      solarCreditAmount={credit}
      calculatedAllocationKw={calculatedAllocationKw}
      originationSourceType={originationSourceType}
    />
  ) : (
    <PersonalEnergyProduction
      billable={billable}
      copy={personalEnergyProductionCopy}
      projectIsLive={true}
      calculatedAllocationKw={calculatedAllocationKw}
      originationSourceType={originationSourceType}
    >
      <EnrollmentTimeline
        status={EnrollmentStatus.Waitlist}
        stateAbbreviation={stateAbbreviation}
        project={project?.name}
        projectStartDate={startDate}
      />
    </PersonalEnergyProduction>
  );

  const mobileGrid = (
    <Grid width="100%" gap="24px">
      <MobileGridCell>
        {flags.lbRewardsEnabled &&
          showRewardsModule(userStatus, userStatusError, userStatusLoading) && (
            <UserRewards userStatus={userStatus} mobile={true} />
          )}
        {personalEnergyProduction}
        <AdvocacyModule
          advocacyCampaign={advocacyCampaign}
          completedAdvocacyCampaign={completedAdvocacyCampaign}
          hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
          margin={{ bottom: '24px' }}
          stateAbbreviation={stateAbbreviation}
          stateFullName={stateFullName}
          utilityAccountId={utilityAccountId}
        />
        {!advocacyCampaign && (
          <ProjectDetails {...projectDetailsProps}>
            <ProjectStatistics
              homesPoweredAnnually={
                project?.acquisitionStatistics?.estimatedHomesPowered
              }
              mwhProducedAnnually={
                (project?.firstYearProductionEstimate || 0) / 1000
              }
            />
            {/* {graph} */}
          </ProjectDetails>
        )}
        <StateSolarFarms
          stateFullName={stateFullName}
          stateId={stateId}
          vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
          vnmSolarProjectId={project?.id}
        />
        {advocacyCampaign && (
          <ProjectDetails
            {...projectDetailsProps}
            margin={{ bottom: '24px' }}
          />
        )}
        <EnergyBreakdown
          zipCode={zipCode}
          small={{ margin: { top: '0px' } }}
          originationSourceType={originationSourceType}
        />
      </MobileGridCell>
    </Grid>
  );

  const desktopGrid = (
    <Grid width="100%" gap="24px">
      <LeftColumnGridCell>
        {personalEnergyProduction}
        <StateSolarFarms
          stateFullName={stateFullName}
          stateId={stateId}
          vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
          vnmSolarProjectId={project?.id}
        />
      </LeftColumnGridCell>
      <RightColumnGridCell>
        {flags.lbRewardsEnabled &&
          showRewardsModule(userStatus, userStatusError, userStatusLoading) && (
            <UserRewards userStatus={userStatus} />
          )}
        <AdvocacyModule
          advocacyCampaign={advocacyCampaign}
          completedAdvocacyCampaign={completedAdvocacyCampaign}
          hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
          margin={{ bottom: '24px' }}
          stateAbbreviation={stateAbbreviation}
          stateFullName={stateFullName}
          utilityAccountId={utilityAccountId}
        />
        <ProjectDetails {...projectDetailsProps}>
          <ProjectStatistics
            homesPoweredAnnually={
              project?.acquisitionStatistics?.estimatedHomesPowered
            }
            mwhProducedAnnually={
              (project?.firstYearProductionEstimate || 0) / 1000
            }
          />
        </ProjectDetails>
        <EnergyBreakdown
          zipCode={zipCode}
          small={{ margin: { top: '0px' } }}
          originationSourceType={originationSourceType}
        />
      </RightColumnGridCell>
    </Grid>
  );

  return (
    <>
      {header}
      <ReconnectUtilityNotification />
      {screenIsSmall(width) ? mobileGrid : desktopGrid}
    </>
  );
};
